import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import Layout from '../Layout';
import { enregistrerBatiment, listerBatiments, nettoyer } from '../api';

const TYPES = [
  ['MAISON', 'Maison'], ['APPARTEMENT', 'Appartement'],
  ['IMMEUBLE', 'Immeuble'], ['COMMERCE', 'Commerce'],
  ['BUREAU', 'Bureau'], ['AUTRE', 'Autre'],
];

/** Seuls ces champs acceptent null ; les autres attendent une chaîne vide. */
const NUMERIQUES = ['construction_year', 'living_area', 'levels'];

const OCCUPATIONS = [
  ['PROPRIETAIRE', "Propriétaire, j'y habite"],
  ['BAILLEUR', 'Propriétaire, je le loue'],
  ['LOCATAIRE', 'Locataire'],
];

/**
 * Deuxième étape : ce que l'assuré sait de son logement.
 *
 * Tout est facultatif, et c'est dit. Personne ne connaît par cœur l'année de
 * construction de sa maison, et exiger la réponse ferait abandonner.
 */
export default function Logement({ expertiseId, onSuivant, onRetour, onEtape }) {
  const [champs, setChamps] = useState({
    type: '', occupation: '', construction_year: '', living_area: '',
    levels: '', roof: '', heating: '', renovations: '',
  });
  const [batimentId, setBatimentId] = useState(null);
  const [erreur, setErreur] = useState('');
  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);

  useEffect(() => {
    let vivant = true;
    listerBatiments(expertiseId)
      .then((liste) => {
        if (!vivant) return;
        const b = liste[0];
        if (b) {
          setBatimentId(b.id);
          setChamps({
            type: b.type || '', occupation: b.occupation || '',
            construction_year: b.construction_year ?? '',
            living_area: b.living_area ?? '', levels: b.levels ?? '',
            roof: b.roof || '', heating: b.heating || '',
            renovations: b.renovations || '',
          });
        }
      })
      .catch(() => vivant && setErreur('Impossible de charger votre logement.'))
      .finally(() => vivant && setChargement(false));
    return () => { vivant = false; };
  }, [expertiseId]);

  const modifier = (nom) => (e) =>
    setChamps((c) => ({ ...c, [nom]: e.target.value }));

  const valider = async () => {
    setEnregistrement(true);
    setErreur('');
    try {
      const charge = nettoyer(champs, NUMERIQUES);
      const b = await enregistrerBatiment(expertiseId, charge, batimentId);
      setBatimentId(b.id);
      onSuivant();
    } catch {
      setErreur("L'enregistrement n'a pas abouti. Réessayez.");
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <Layout
      etape="logement"
      onEtape={onEtape}
      titre="Votre logement"
      introduction="Ce que vous en savez suffit. Laissez vide ce que vous ignorez — ces informations nous aident à proposer des réparations adaptées, elles ne conditionnent rien."
      actions={
        <>
          <Button onClick={onRetour} disabled={enregistrement}>Retour</Button>
          <Button variant="contained" size="large" onClick={valider}
                  disabled={chargement || enregistrement}>
            Continuer
          </Button>
        </>
      }
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}

      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 } }}>
        <Stack spacing={4}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <TextField select label="Type de bien" value={champs.type}
                       onChange={modifier('type')} disabled={chargement}>
              {TYPES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
            </TextField>
            <TextField select label="Vous êtes" value={champs.occupation}
                       onChange={modifier('occupation')} disabled={chargement}>
              {OCCUPATIONS.map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </TextField>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <TextField label="Année de construction" type="number"
                       value={champs.construction_year}
                       onChange={modifier('construction_year')}
                       disabled={chargement}
                       helperText="Approximative, si vous la connaissez." />
            <TextField label="Surface habitable" type="number"
                       value={champs.living_area}
                       onChange={modifier('living_area')} disabled={chargement}
                       InputProps={{ endAdornment: 'm²' }} />
            <TextField label="Nombre de niveaux" type="number"
                       value={champs.levels} onChange={modifier('levels')}
                       disabled={chargement} />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <TextField label="Toiture" value={champs.roof}
                       onChange={modifier('roof')} disabled={chargement}
                       placeholder="tuiles, ardoises, toit plat…" />
            <TextField label="Chauffage" value={champs.heating}
                       onChange={modifier('heating')} disabled={chargement}
                       placeholder="mazout, gaz, pompe à chaleur…" />
          </Stack>

          <TextField label="Travaux ou rénovations connus" multiline minRows={2}
                     value={champs.renovations}
                     onChange={modifier('renovations')} disabled={chargement}
                     helperText="Une toiture refaite ou une salle de bain rénovée change l'état des lieux." />
        </Stack>
      </Paper>
    </Layout>
  );
}
